import { supabase } from '../lib/supabase';

export const paymentReminderService = {
  /**
   * Get all overdue invoices for a company
   */
  async getOverdueInvoices(companyId) {
    try {
      const today = new Date()?.toISOString()?.split('T')?.[0];
      
      const { data, error } = await supabase
        ?.from('stripe_invoices')
        ?.select(`
          *,
          subscription_billing_history:subscription_billing_history_id (
            tier_at_billing,
            employee_count_at_billing
          )
        `)
        ?.eq('company_id', companyId)
        ?.eq('status', 'open')
        ?.lt('due_date', today)
        ?.order('due_date', { ascending: true });

      if (error) throw error;

      return data?.map(invoice => ({
        id: invoice?.id,
        stripeInvoiceId: invoice?.stripe_invoice_id,
        invoiceNumber: invoice?.invoice_number,
        status: invoice?.status,
        amountDue: invoice?.amount_due,
        amountPaid: invoice?.amount_paid,
        currency: invoice?.currency,
        billingPeriodStart: invoice?.billing_period_start,
        billingPeriodEnd: invoice?.billing_period_end,
        dueDate: invoice?.due_date,
        hostedInvoiceUrl: invoice?.hosted_invoice_url,
        invoicePdfUrl: invoice?.invoice_pdf_url,
        tierAtBilling: invoice?.subscription_billing_history?.tier_at_billing,
        employeeCountAtBilling: invoice?.subscription_billing_history?.employee_count_at_billing,
        daysOverdue: this.calculateDaysOverdue(invoice?.due_date),
        reminderSentAt: invoice?.metadata?.reminder_sent_at,
        reminderCount: invoice?.metadata?.reminder_count || 0,
        createdAt: invoice?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching overdue invoices:', error);
      throw error;
    }
  },

  /**
   * Calculate days overdue
   */
  calculateDaysOverdue(dueDate) {
    if (!dueDate) return 0;
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = today - due;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  },

  /**
   * Send payment reminder email
   */
  async sendPaymentReminder(invoice, company, reminderType = 'standard') {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      
      const response = await fetch(
        `${supabase?.supabaseUrl}/functions/v1/send-payment-reminder`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({
            invoice,
            company,
            reminderType
          })
        }
      );

      if (!response?.ok) {
        const errorData = await response?.json();
        throw new Error(errorData?.error || 'Failed to send payment reminder');
      }

      const result = await response?.json();

      // Update invoice metadata with reminder info
      await this.updateInvoiceReminderMetadata(invoice?.id, {
        reminder_sent_at: new Date()?.toISOString(),
        reminder_count: (invoice?.reminderCount || 0) + 1,
        last_reminder_type: reminderType
      });

      // Queue notification
      await this.queueReminderNotification(invoice, company, result);

      return result;
    } catch (error) {
      console.error('Error sending payment reminder:', error);
      throw error;
    }
  },

  /**
   * Update invoice metadata with reminder tracking
   */
  async updateInvoiceReminderMetadata(invoiceId, reminderData) {
    try {
      const { data: invoice } = await supabase
        ?.from('stripe_invoices')
        ?.select('metadata')
        ?.eq('id', invoiceId)
        ?.single();

      const updatedMetadata = {
        ...(invoice?.metadata || {}),
        ...reminderData
      };

      const { error } = await supabase
        ?.from('stripe_invoices')
        ?.update({ metadata: updatedMetadata })
        ?.eq('id', invoiceId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating invoice reminder metadata:', error);
    }
  },

  /**
   * Queue reminder notification in notification_queue
   */
  async queueReminderNotification(invoice, company, emailResult) {
    try {
      const { error } = await supabase
        ?.from('notification_queue')
        ?.insert({
          notification_type: 'system_alert',
          priority: invoice?.daysOverdue > 30 ? 'critical' : 'high',
          recipient_email: company?.contactEmail,
          subject: `Payment Reminder: Invoice ${invoice?.invoiceNumber}`,
          message: `Payment reminder sent for invoice ${invoice?.invoiceNumber} (${invoice?.daysOverdue} days overdue)`,
          entity_type: 'stripe_invoice',
          entity_id: invoice?.id,
          metadata: {
            invoice_number: invoice?.invoiceNumber,
            amount_due: invoice?.amountDue,
            days_overdue: invoice?.daysOverdue,
            email_message_id: emailResult?.messageId
          },
          notification_status: 'sent',
          sent_at: new Date()?.toISOString()
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error queueing reminder notification:', error);
    }
  },

  /**
   * Send bulk reminders for all overdue invoices
   */
  async sendBulkReminders(companyId, company) {
    try {
      const overdueInvoices = await this.getOverdueInvoices(companyId);
      
      if (overdueInvoices?.length === 0) {
        return { message: 'No overdue invoices found', sent: 0 };
      }

      const results = [];
      for (const invoice of overdueInvoices) {
        try {
          // Determine reminder type based on days overdue
          let reminderType = 'standard';
          if (invoice?.daysOverdue > 30) {
            reminderType = 'final';
          } else if (invoice?.daysOverdue > 14) {
            reminderType = 'urgent';
          }

          const result = await this.sendPaymentReminder(invoice, company, reminderType);
          results?.push({ invoiceId: invoice?.id, success: true, result });
        } catch (err) {
          results?.push({ invoiceId: invoice?.id, success: false, error: err?.message });
        }
      }

      return {
        total: overdueInvoices?.length,
        sent: results?.filter(r => r?.success)?.length,
        failed: results?.filter(r => !r?.success)?.length,
        details: results
      };
    } catch (error) {
      console.error('Error sending bulk reminders:', error);
      throw error;
    }
  },

  /**
   * Get payment reconciliation status
   */
  async getPaymentReconciliationStatus(companyId) {
    try {
      const { data: invoices, error: invoicesError } = await supabase
        ?.from('stripe_invoices')
        ?.select('*')
        ?.eq('company_id', companyId)
        ?.order('created_at', { ascending: false });

      if (invoicesError) throw invoicesError;

      const { data: transactions, error: transactionsError } = await supabase
        ?.from('payment_transactions')
        ?.select('*')
        ?.eq('company_id', companyId)
        ?.order('created_at', { ascending: false });

      if (transactionsError) throw transactionsError;

      // Calculate reconciliation metrics
      const totalInvoices = invoices?.length || 0;
      const paidInvoices = invoices?.filter(inv => inv?.status === 'paid')?.length || 0;
      const openInvoices = invoices?.filter(inv => inv?.status === 'open')?.length || 0;
      const overdueInvoices = invoices?.filter(inv => {
        if (inv?.status !== 'open') return false;
        const daysOverdue = this.calculateDaysOverdue(inv?.due_date);
        return daysOverdue > 0;
      })?.length || 0;

      const totalTransactions = transactions?.length || 0;
      const successfulTransactions = transactions?.filter(t => t?.status === 'succeeded')?.length || 0;
      const pendingTransactions = transactions?.filter(t => t?.status === 'pending' || t?.status === 'processing')?.length || 0;
      const failedTransactions = transactions?.filter(t => t?.status === 'failed')?.length || 0;

      // Reconciliation status breakdown
      const reconciledPayments = transactions?.filter(t => t?.reconciliation_status === 'reconciled')?.length || 0;
      const pendingReconciliation = transactions?.filter(t => t?.reconciliation_status === 'pending')?.length || 0;
      const processingReconciliation = transactions?.filter(t => t?.reconciliation_status === 'processing')?.length || 0;
      const failedReconciliation = transactions?.filter(t => t?.reconciliation_status === 'failed')?.length || 0;

      // Payment method breakdown
      const stripePayments = transactions?.filter(t => t?.payment_method === 'stripe')?.length || 0;
      const bspPayments = transactions?.filter(t => t?.payment_method === 'bsp')?.length || 0;
      const kinabankPayments = transactions?.filter(t => t?.payment_method === 'kinabank')?.length || 0;
      const creditbankPayments = transactions?.filter(t => t?.payment_method === 'creditbank')?.length || 0;

      const totalRevenue = invoices?.reduce((sum, inv) => sum + (parseFloat(inv?.amount_paid) || 0), 0);
      const outstandingAmount = invoices?.filter(inv => inv?.status === 'open')?.reduce((sum, inv) => sum + (parseFloat(inv?.amount_due) || 0), 0);

      return {
        invoices: {
          total: totalInvoices,
          paid: paidInvoices,
          open: openInvoices,
          overdue: overdueInvoices
        },
        transactions: {
          total: totalTransactions,
          successful: successfulTransactions,
          pending: pendingTransactions,
          failed: failedTransactions
        },
        reconciliation: {
          reconciled: reconciledPayments,
          pending: pendingReconciliation,
          processing: processingReconciliation,
          failed: failedReconciliation,
          reconciliationRate: totalTransactions > 0 ? ((reconciledPayments / totalTransactions) * 100)?.toFixed(1) : 0
        },
        paymentMethods: {
          stripe: stripePayments,
          bsp: bspPayments,
          kinabank: kinabankPayments,
          creditbank: creditbankPayments
        },
        financial: {
          totalRevenue: totalRevenue?.toFixed(2),
          outstandingAmount: outstandingAmount?.toFixed(2),
          collectionRate: totalRevenue > 0 ? ((totalRevenue / (totalRevenue + outstandingAmount)) * 100)?.toFixed(1) : 0
        },
        lastUpdated: new Date()?.toISOString()
      };
    } catch (error) {
      console.error('Error calculating payment reconciliation status:', error);
      throw error;
    }
  },

  /**
   * Subscribe to real-time payment updates
   */
  subscribeToPaymentUpdates(companyId, callback) {
    const channel = supabase
      ?.channel('payment-updates')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'stripe_invoices',
          filter: `company_id=eq.${companyId}`
        },
        (payload) => {
          callback({ type: 'invoice', payload });
        }
      )
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payment_transactions',
          filter: `company_id=eq.${companyId}`
        },
        (payload) => {
          callback({ type: 'transaction', payload });
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Unsubscribe from real-time updates
   */
  unsubscribeFromPaymentUpdates(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};

export default paymentReminderService;